import { NULL_FUNCTION } from './common';

const wait = (delay: number, cancel: Promise<any> = new Promise(NULL_FUNCTION)) => {
    let id, both = x => [x, x];
    cancel = cancel.then(...both(() => Promise.reject(new AsyncTaskCancelError())));
    cancel.catch(() => clearTimeout(id));
    return Promise.race([new Promise(resolve => id = setTimeout(resolve, Math.max(0, delay))), cancel]);
};

class AsyncTaskCancelError {

    public name: string = "AsyncTaskCancelError";

    get error(): Error {
        return new Error("Task cancelled");
    }

}

class CancelToken {

    public reason: string = "System cancelled";
    resolver: Function = NULL_FUNCTION;
    promise: Promise<any>;

    constructor() {
        this.promise = new Promise(resolve => this.resolver = resolve);
    }

    cancel(reason?) {
        this.reason = reason || this.reason;
        this.resolver(reason);
    }

}

type TIMING = "micro" | "ondemand";
const TIMING_MAP = {
    micro: 0,
    ondemend: -1
};

export class AsyncTask {

    public static NULL_TASK = new AsyncTask(() => null, "micro", "NullAsyncTask");

    name: string;
    private cancelToken: CancelToken;
    private task: Function;
    private delay: number;

    constructor(task: Function, delay?: number | TIMING, name?: string) {
        this.task = function () {
            return new Promise(resolve => {
                let result = task();
                if (result instanceof Promise) {
                    result.then(resolve);
                } else {
                    resolve(result);
                }
            })
        };

        if (typeof delay == "number") {
            this.delay = delay;
        } else {
            this.delay = TIMING_MAP[delay || "micro"];
        }

        this.name = name || "";
        if (name) this.cancelToken = new CancelToken();
    }

    run(): Promise<any> {
        if (this.cancelToken) {
            return Promise.resolve()
                .then(() => wait(this.delay, this.cancelToken.promise))
                .then(() => this.task())
                .catch(e => e.name == "AsyncTaskCancelError" ? console.debug("AsyncTask canceled:", this.cancelToken.reason) : Promise.reject(e))
                .catch(e => console.error(e));
        }
        return Promise.resolve()
            .then(() => waitFor(this.delay))
            .then(() => this.task())
            .catch(e => e.name == "AsyncTaskCancelError" ? console.debug("AsyncTask canceled:", this.cancelToken.reason) : Promise.reject(e))
            .catch(e => console.error(e));
    }

    cancel(reason: string) {
        if (!this.cancelToken) {
            console.warn("Unnamed AsyncTask cannot be canceled")
            return;
        }
        if (this.delay <= 0 && this.delay != 0) {
            console.warn("AsyncTask with micro task timing cannot be cancelled");
            return;
        }

        this.cancelToken.cancel(reason);
    }

}

export class AsyncTaskTracker {

    public maxSize: number = 3;
    private tasks: object = {};

    track(asyncTask: AsyncTask) {
        if (!asyncTask || !asyncTask.name) {
            return;
        }
        if (this.names().length == this.maxSize) {
            delete this.tasks[this.first().name];
        }
        this.tasks[asyncTask.name] = asyncTask;
    }

    cancel(taskName: string, reason?: string) {
        let asyncTask = this.tasks[taskName];
        asyncTask && asyncTask.cancel(reason);
    }

    cancelAll(reason?: string) {
        for (let name in this.tasks) {
            this.cancel(this.tasks[name], reason);
        }
    }

    names(): string[] {
        return Object.keys(this.tasks);
    }

    first(): AsyncTask {
        return this.tasks[this.names()[0]];
    }

    last(): AsyncTask {
        return this.tasks[this.names()[this.names().length - 1]];
    }


}

export function waitFor(delay) {
    return new Promise(resolve => setTimeout(resolve, delay));
}

